/**
 * Stammdaten — the CRA master data in CMDBuild, edited here instead of there.
 *
 * This page is the reason nobody has to open the CMDBuild web UI: it lists every
 * card of a class, creates, edits and deletes them, and renders each form purely
 * from `GET /api/cmdb/meta`. Adding an attribute to
 * `app/services/schema_service.py::_SCHEMA` makes it appear here with no change
 * to this file.
 *
 * Contract details that shape the code:
 *   - `rows[].display` is already German and reference-resolved, so the table
 *     never formats anything itself; `rows[].values` is what the form edits.
 *   - The reference pickers need the option lists of the *target* classes, so
 *     they are fetched when a dialog opens (batched into one request) rather than
 *     on page load — the options of eleven classes are a lot of cards.
 *   - Edit and delete re-read the single card first. That read is the only source
 *     of `referenced_by`, which the delete dialog must show, and it also means the
 *     form starts from server state rather than from a possibly stale list row.
 *   - Writes are gated on `cmdb.write`; an auditor sees every list and detail and
 *     no enabled action.
 *   - A count of `-1` means the class could not be read at all (schema not
 *     provisioned) — displayed as '?' instead of a wrong zero.
 *
 * Deep link: `/stammdaten?klasse=ProduktInstanz&id=42` selects the class, expands
 * that card and scrolls it into view. Other pages link here instead of repeating
 * the master data — the CVE view on „Schwachstellen“ does it for every affected
 * instance. Reference (foreign key) values inside this page use the very same
 * link, so a chain like ProduktInstanz → ProduktVersion → ProduktType is walked
 * by clicking, with the browser's back button undoing each step.
 */

import { i18n as globalI18n, type MessageDescriptor } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import { Plural, Trans, useLingui } from '@lingui/react/macro';
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { CveText } from '../components/CveLink';
import Banner from '../components/Banner';
import Chip from '../components/Chip';
import DataTable, { type DataTableColumn, type RowKey } from '../components/DataTable';
import EmptyState from '../components/EmptyState';
import Icon from '../components/Icon';
import ManufacturerLogo from '../components/ManufacturerLogo';
import PageHeader from '../components/PageHeader';
import { PageSpinner } from '../components/Spinner';
import { useToast } from '../components/Toast';
import {
  createCmdbCard,
  deleteCmdbCard,
  errorMessage,
  fetchCmdbCard,
  fetchCmdbCards,
  fetchCmdbCounts,
  fetchCmdbMeta,
  fetchCmdbOptions,
  updateCmdbCard,
} from '../lib/api';
import { useAuth } from '../lib/auth';
import { DASH, fmtDate, fmtDay } from '../lib/format';
import { readOnlyNotice } from '../lib/permissions';
import type {
  CmdbAttribute,
  CmdbCardRow,
  CmdbClassMeta,
  CmdbMetaResponse,
  CmdbOption,
} from '../lib/types';

import CardFormModal from './stammdaten/CardFormModal';
import { classHint } from './stammdaten/classHints';
import BomOverview from './stammdaten/BomOverview';
import ComponentCatalog from './stammdaten/ComponentCatalog';
import DeleteCardModal from './stammdaten/DeleteCardModal';
import { listAttributes, referenceLink, referenceTargets } from './stammdaten/cardHelpers';
import ProductCatalog from './stammdaten/ProductCatalog';
import { SBOM_UPLOAD_CLASSES, sbomUploadLink, sbomUploadTitle } from './stammdaten/sbomUpload';
import StammIcon, { type StammIconName } from './stammdaten/StammIcon';

import './stammdaten/stammdaten.css';
import './stammdaten/component-catalog.css';
import './stammdaten/product-catalog.css';
import './stammdaten/bom.css';

/**
 * Sidebar grouping. Purely presentational — the class *order* is the server's
 * (`CLASS_ORDER` in cmdb_crud.py); anything not listed here lands under "Weitere"
 * so a new class is never invisible.
 */
const GROUPS: readonly { key: string; label: MessageDescriptor; classes: readonly string[] }[] = [
  {
    key: 'vendor',
    label: msg`Vendor-Katalog`,
    classes: [
      'ProduktType',
      'ProduktVersion',
      'ProduktVersionKomponente',
      'KomponenteType',
      'KomponenteVersion',
    ],
  },
  { key: 'delivery', label: msg`Auslieferung`, classes: ['Kunde', 'ProduktInstanz', 'Country'] },
  { key: 'vulnerabilities', label: msg`Schwachstellen`, classes: ['CVE', 'VulnerabilityEvent', 'Benachrichtigung', 'NotificationInstance'] },
  { key: 'responsibility', label: msg`Verantwortlichkeit`, classes: ['PsirtTeam', 'ProduktVerantwortung'] },
];

const CLASS_ICONS: Readonly<Record<string, StammIconName>> = {
  ProduktType: 'box', ProduktVersion: 'layers', KomponenteType: 'component',
  KomponenteVersion: 'layers', ProduktVersionKomponente: 'link', Kunde: 'users',
  ProduktInstanz: 'box', CVE: 'shield',
  VulnerabilityEvent: 'shield', Benachrichtigung: 'bell', NotificationInstance: 'link',
  PsirtTeam: 'users', ProduktVerantwortung: 'link', Country: 'globe',
};


/** The attribute a class's row name is built from; listing it again as a column would repeat the first column. */
const NAME_ATTRIBUTE: Readonly<Record<string, string>> = {
  Kunde: 'Name', ProduktType: 'Bezeichnung', KomponenteType: 'Bezeichnung', CVE: 'CveId', PsirtTeam: 'Name',
  Country: 'Name',
};

// CMDB CVE cards are a stored subset (manual/imported or matched KEV hits).
// The complete scan findings remain in the separate Dependency-Track view.
const STORED_CVES = msg`Gespeicherte CVEs`;
function catalogLabel(meta: CmdbClassMeta): string {
  return meta.name === 'CVE' ? globalI18n._(STORED_CVES) : meta.label_plural;
}

/**
 * A reference value rendered as a jump into its target class.
 *
 * `stopPropagation` is required: the cell lives inside a clickable table row, and
 * without it the row would toggle while the router navigates. Clearing the search
 * term is the same reason a class switch clears it — the target card must not be
 * filtered out of the list it is being linked into.
 */
function ReferenceValue({
  klass,
  id,
  label,
  targetLabel,
  onNavigate,
}: {
  klass: string;
  id: number;
  label: string;
  targetLabel: string;
  onNavigate: () => void;
}): ReactNode {
  const { t } = useLingui();
  return (
    <Link
      className="stamm-ref"
      to={`/stammdaten?klasse=${encodeURIComponent(klass)}&id=${id}`}
      title={t`${targetLabel} „${label}“ öffnen`}
      onClick={(event) => {
        event.stopPropagation();
        onNavigate();
      }}
    >
      <span>{label}</span>
      <Icon name="external" size={12} className="stamm-ref-icon" />
    </Link>
  );
}

type DialogState =
  | { kind: 'closed' }
  | { kind: 'form'; row: CmdbCardRow | null; options: Record<string, CmdbOption[]> }
  | { kind: 'delete'; row: CmdbCardRow };

export function StammdatenPage(): ReactNode {
  const { has } = useAuth();
  const toast = useToast();
  const { t, i18n } = useLingui();
  const canWrite = has('cmdb.write');
  const canImport = has('import.write');

  const [meta, setMeta] = useState<CmdbMetaResponse | null>(null);
  // "Über diese Daten": the class hint, opened from the info button next to the title.
  const [infoOpen, setInfoOpen] = useState(false);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [selected, setSelected] = useState<string>('ProduktInstanz');
  // Another class has another hint; start closed again.
  useEffect(() => setInfoOpen(false), [selected]);
  const [rows, setRows] = useState<CmdbCardRow[]>([]);
  const [search, setSearch] = useState('');
  const [metaError, setMetaError] = useState<string | null>(null);
  const [listError, setListError] = useState<string | null>(null);
  const [loadingMeta, setLoadingMeta] = useState(true);
  const [loadingRows, setLoadingRows] = useState(false);
  const [dialogBusy, setDialogBusy] = useState(false);
  const [dialog, setDialog] = useState<DialogState>({ kind: 'closed' });

  /* --- deep link (?klasse=&id=) ----------------------------------------- *
   * A link from another page names the class and the card. The class switch is
   * an effect (the sidebar is state, not the URL); the id is kept so the row can
   * be expanded and scrolled to once the list has arrived.                    */

  const [searchParams, setSearchParams] = useSearchParams();
  const focusId = Number(searchParams.get('id')) || null;
  const [expanded, setExpanded] = useState<ReadonlySet<RowKey>>(new Set());
  // The highlighted card. Separate from `focusId` on purpose: the highlight marks
  // "here is what you followed the link for" and must fade on the next click,
  // while the URL keeps the link shareable.
  const [focusRow, setFocusRow] = useState<number | null>(null);
  // Keyed by class *and* id: two different classes can hold the same card id, and
  // a jump between them must still expand the target.
  const focusedRef = useRef<string | null>(null);
  const catalogRef = useRef<HTMLElement>(null);

  // Guards a late list response from overwriting a newer one (class switching
  // and typing in the search box both fire loads).
  const listSeq = useRef(0);
  const dialogSeq = useRef(0);

  const classMeta: CmdbClassMeta | null = useMemo(
    () => meta?.classes.find((entry) => entry.name === selected) ?? null,
    [meta, selected],
  );
  const hint = classHint(classMeta?.name);
  const hasComponentCatalog = !!meta?.classes.some((entry) => entry.name === 'KomponenteType')
    && !!meta?.classes.some((entry) => entry.name === 'KomponenteVersion');
  const isComponentCatalog = hasComponentCatalog && ['KomponenteType', 'KomponenteVersion'].includes(selected);
  // Products own their versions the same way (DEV-111).
  const hasProductCatalog = !!meta?.classes.some((entry) => entry.name === 'ProduktType')
    && !!meta?.classes.some((entry) => entry.name === 'ProduktVersion');
  const isProductCatalog = hasProductCatalog && ['ProduktType', 'ProduktVersion'].includes(selected);
  const catalogSelection = isComponentCatalog ? 'KomponenteType' : isProductCatalog ? 'ProduktType' : selected;
  // One entry per product version instead of one row per Stücklisteneintrag (DEV-114).
  const isBomOverview = selected === 'ProduktVersionKomponente';

  // On compact screens the catalog becomes a horizontal rail. Keep the active
  // category visible without moving the surrounding page vertically.
  useEffect(() => {
    const catalog = catalogRef.current;
    const active = catalog?.querySelector<HTMLElement>('[aria-current="page"]');
    if (!catalog || !active || catalog.scrollWidth <= catalog.clientWidth) return;
    const rail = catalog.getBoundingClientRect();
    const item = active.getBoundingClientRect();
    if (item.left < rail.left || item.right > rail.right) {
      catalog.scrollLeft += item.left - rail.left - (rail.width - item.width) / 2;
    }
  }, [selected, loadingMeta]);

  /* --- loading ---------------------------------------------------------- */

  const loadCounts = useCallback(async (): Promise<void> => {
    try {
      const response = await fetchCmdbCounts();
      setCounts(response.counts);
    } catch (error) {
      // Counts are decoration; a failure must not hide the data itself.
      setCounts({});
      void error;
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setLoadingMeta(true);
      try {
        const response = await fetchCmdbMeta();
        if (cancelled) return;
        setMeta(response);
        setMetaError(null);
        setSelected((current) =>
          response.classes.some((entry) => entry.name === current)
            ? current
            : (response.classes[0]?.name ?? current),
        );
      } catch (error) {
        if (!cancelled) setMetaError(errorMessage(error));
      } finally {
        if (!cancelled) setLoadingMeta(false);
      }
      if (!cancelled) await loadCounts();
    })();
    return () => {
      cancelled = true;
    };
  }, [loadCounts]);

  const loadRows = useCallback(
    async (klass: string, term: string): Promise<void> => {
      const seq = ++listSeq.current;
      setLoadingRows(true);
      try {
        const response = await fetchCmdbCards(klass, term);
        if (seq !== listSeq.current) return;
        setRows(response.rows);
        setListError(null);
      } catch (error) {
        if (seq !== listSeq.current) return;
        setRows([]);
        setListError(errorMessage(error));
      } finally {
        if (seq === listSeq.current) setLoadingRows(false);
      }
    },
    [],
  );

  // Debounced: the search filter runs server-side, so every keystroke would
  // otherwise be a full class scan in CMDBuild.
  useEffect(() => {
    if (!classMeta || isComponentCatalog || isProductCatalog || isBomOverview) return;
    const handle = window.setTimeout(() => {
      void loadRows(classMeta.name, search);
    }, search === '' ? 0 : 300);
    return () => window.clearTimeout(handle);
  }, [classMeta, isBomOverview, isComponentCatalog, isProductCatalog, loadRows, search]);

  const selectClass = useCallback((klass: string): void => {
    ++listSeq.current;
    ++dialogSeq.current;
    focusedRef.current = null;
    setDialogBusy(false);
    setDialog({ kind: 'closed' });
    setSelected(klass);
    setSearch('');
    setRows([]);
    setListError(null);
    setExpanded(new Set());
    setFocusRow(null);
  }, []);

  const navigateClass = useCallback((klass: string): void => {
    if (klass !== selected) {
      selectClass(klass);
    } else {
      setSearch('');
      setExpanded(new Set());
      setFocusRow(null);
      focusedRef.current = null;
    }
    setSearchParams({ klasse: klass });
  }, [selectClass, selected, setSearchParams]);

  // The linked class wins over whatever the sidebar had selected. Guarded on the
  // meta list so a stale link cannot select a class that does not exist.
  const linkedClass = searchParams.get('klasse');
  useEffect(() => {
    if (!linkedClass || !meta || linkedClass === selected) return;
    if (!meta.classes.some((entry) => entry.name === linkedClass)) return;
    // Same reset as a sidebar click: a search term left over from the previous
    // class would filter the linked card out of the list.
    selectClass(linkedClass);
  }, [linkedClass, meta, selectClass, selected]);

  // Expand and scroll to the linked card, once — the user must be able to
  // collapse it again without it snapping back open on the next render.
  useEffect(() => {
    if (focusId === null) return;
    if (linkedClass && linkedClass !== selected) return;
    const key = `${selected}:${focusId}`;
    if (focusedRef.current === key) return;
    if (!rows.some((row) => row.id === focusId)) return;
    focusedRef.current = key;
    setExpanded(new Set([focusId]));
    setFocusRow(focusId);
    // The row exists in the DOM only after this render commits.
    const handle = window.setTimeout(() => {
      document
        .querySelector('.stamm-focus')
        ?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }, 0);
    return () => window.clearTimeout(handle);
  }, [focusId, linkedClass, rows, selected]);

  /**
   * Drop the link highlight — any click elsewhere means the user has arrived.
   *
   * The `id` param goes with it (and `focusedRef` is reset), so following the very
   * same link again highlights again instead of being swallowed by the once-guard.
   */
  const clearFocus = useCallback((): void => {
    setFocusRow(null);
    focusedRef.current = null;
    setSearchParams(
      (current) => {
        if (!current.has('id')) return current;
        const next = new URLSearchParams(current);
        next.delete('id');
        return next;
      },
      { replace: true },
    );
  }, [setSearchParams]);

  const toggleRow = useCallback(
    (key: RowKey): void => {
      clearFocus();
      setExpanded((current) => {
        const next = new Set(current);
        if (next.has(key)) next.delete(key);
        else next.add(key);
        return next;
      });
    },
    [clearFocus],
  );

  /* --- dialogs ---------------------------------------------------------- */

  const openForm = useCallback(
    async (row: CmdbCardRow | null): Promise<void> => {
      if (!classMeta) return;
      const seq = ++dialogSeq.current;
      setDialogBusy(true);
      try {
        const targets = referenceTargets(classMeta);
        const [options, fresh] = await Promise.all([
          targets.length > 0 ? fetchCmdbOptions(targets) : Promise.resolve({ options: {} }),
          row ? fetchCmdbCard(classMeta.name, row.id) : Promise.resolve(null),
        ]);
        if (seq === dialogSeq.current) setDialog({ kind: 'form', row: fresh ?? row, options: options.options });
      } catch (error) {
        if (seq === dialogSeq.current) toast.error(errorMessage(error));
      } finally {
        if (seq === dialogSeq.current) setDialogBusy(false);
      }
    },
    [classMeta, toast],
  );

  const openDelete = useCallback(
    async (row: CmdbCardRow): Promise<void> => {
      if (!classMeta) return;
      const seq = ++dialogSeq.current;
      setDialogBusy(true);
      try {
        const fresh = await fetchCmdbCard(classMeta.name, row.id);
        if (seq === dialogSeq.current) setDialog({ kind: 'delete', row: fresh });
      } catch (error) {
        if (seq === dialogSeq.current) toast.error(errorMessage(error));
      } finally {
        if (seq === dialogSeq.current) setDialogBusy(false);
      }
    },
    [classMeta, toast],
  );

  const afterWrite = useCallback(
    async (message: string): Promise<void> => {
      setDialog({ kind: 'closed' });
      toast.success(message);
      if (classMeta) await loadRows(classMeta.name, search);
      await loadCounts();
    },
    [classMeta, loadCounts, loadRows, search, toast],
  );

  const submitForm = useCallback(
    async (values: Record<string, unknown>): Promise<void> => {
      if (!classMeta || dialog.kind !== 'form') return;
      const editing = dialog.row;
      const classLabel = classMeta.label;
      if (editing) {
        const savedLabel = (await updateCmdbCard(classMeta.name, editing.id, values)).label;
        await afterWrite(t`${classLabel} „${savedLabel}“ gespeichert.`);
      } else {
        const savedLabel = (await createCmdbCard(classMeta.name, values)).label;
        await afterWrite(t`${classLabel} „${savedLabel}“ angelegt.`);
      }
    },
    [afterWrite, classMeta, dialog, t],
  );

  const confirmDelete = useCallback(
    async (cascade: boolean): Promise<void> => {
      if (!classMeta || dialog.kind !== 'delete') return;
      const target = dialog.row;
      await deleteCmdbCard(classMeta.name, target.id, cascade);
      const classLabel = classMeta.label;
      const deletedLabel = target.label;
      await afterWrite(t`${classLabel} „${deletedLabel}“ gelöscht.`);
    },
    [afterWrite, classMeta, dialog, t],
  );

  /* --- values ----------------------------------------------------------- */

  /**
   * One attribute value, for the table cell and the detail list alike.
   *
   * A reference becomes a link to the card it points at — `row.display` already
   * holds its German label, `row.values` the card id the link needs. Classes the
   * server did not send (not provisioned) stay plain text: the deep link would
   * select nothing.
   */
  const renderValue = useCallback(
    (attr: CmdbAttribute, row: CmdbCardRow): ReactNode => {
      const value = row.display[attr.name] ?? '';
      const reference = referenceLink(attr, row);
      if (reference && meta?.classes.some((entry) => entry.name === reference.klass)) {
        const target = meta.classes.find((entry) => entry.name === reference.klass);
        return (
          <ReferenceValue
            klass={reference.klass}
            id={reference.id}
            label={value === '' ? `#${reference.id}` : value}
            targetLabel={target?.label ?? reference.klass}
            onNavigate={() => setSearch('')}
          />
        );
      }
      if (['CveId', 'KevCves', 'Cves'].includes(attr.name)) return <CveText text={value} />;
      if (value === '') return <span className="muted">{DASH}</span>;
      if (attr.type === 'date') return fmtDay(value);
      if (attr.type === 'dateTime') return fmtDate(value);
      return value;
    },
    [meta],
  );

  /* --- table ------------------------------------------------------------ */

  const columns = useMemo((): readonly DataTableColumn<CmdbCardRow>[] => {
    if (!classMeta) return [];
    const isInstance = classMeta.name === 'ProduktInstanz';
    // Put relationships first; full technical identifiers remain in the details.
    const attrs = isInstance
      ? ['KundeID', 'AktuelleVersionID', 'AuslieferDatum'].flatMap((name) =>
          classMeta.attributes.filter((attr) => attr.name === name))
      : listAttributes(classMeta).filter((attr) => attr.name !== NAME_ATTRIBUTE[classMeta.name]).slice(0, 4);
    const attrColumns: DataTableColumn<CmdbCardRow>[] = attrs.map((attr) => ({
      key: attr.name,
      header: attr.name === 'AuslieferDatum' ? t`Auslieferung` : attr.label,
      headerTitle: attr.help,
      sortValue: (row) => row.display[attr.name] ?? '',
      width: attr.type === 'date' ? '14%' : undefined,
      render: (row) => <div className="stamm-cell-value">{renderValue(attr, row)}</div>,
    }));
    if (classMeta.name === 'KomponenteType') {
      const manufacturerIndex = attrColumns.findIndex((column) => column.key === 'Hersteller');
      attrColumns.splice(manufacturerIndex < 0 ? attrColumns.length : manufacturerIndex, 0, {
        key: '__manufacturerLogo',
        header: t`Herstellerlogo`,
        width: '15%',
        align: 'center',
        render: (row) => <ManufacturerLogo manufacturer={row.display.Hersteller} />,
      });
    }
    return [
      {
        key: '__label',
        header: isInstance ? t`Produkt / Seriennummer` : t`Bezeichnung`,
        width: isInstance ? '31%' : '28%',
        sortValue: (row) => row.label.toLowerCase(),
        render: (row) => (
          <div className="stamm-record">
            <span className="stamm-record-icon"><StammIcon name={CLASS_ICONS[classMeta.name] ?? 'database'} /></span>
            <div className="stamm-record-text">
              <strong title={isInstance ? row.label : undefined}>{isInstance ? row.display.ProduktTypeID || row.label : row.label}</strong>
              {isInstance ? <span className="stamm-serial">{row.display.Seriennummer || t`Ohne Seriennummer`}</span> : null}
            </div>
          </div>
        ),
      },
      ...attrColumns,
      {
        key: '__actions',
        header: <span className="visually-hidden"><Trans>Aktionen</Trans></span>,
        mobileLabel: t`Aktionen`,
        width: SBOM_UPLOAD_CLASSES.has(classMeta.name) && canImport ? '132px' : '88px',
        align: 'right',
        cellClassName: 'stamm-action-cell',
        render: (row) => {
          const { label } = row;
          return <div className="stamm-actions">
            {canImport && sbomUploadLink(classMeta.name, row) !== null ? (
              // SBOM upload onto this product, version or instance (DEV-107).
              <Link
                className="stamm-action-button"
                to={sbomUploadLink(classMeta.name, row) ?? ''}
                aria-label={t`SBOM für ${label} hochladen`}
                title={sbomUploadTitle(classMeta.name, row)}
                onClick={(event) => event.stopPropagation()}
              >
                <Icon name="upload" size={18} />
              </Link>
            ) : null}
            <button
              type="button"
              className="stamm-action-button"
              disabled={!canWrite || dialogBusy}
              aria-label={t`${label} bearbeiten`}
              title={canWrite ? t`Bearbeiten` : readOnlyNotice('cmdb.write')}
              onClick={(event) => {
                event.stopPropagation();
                void openForm(row);
              }}
            >
              <Icon name="edit" size={18} />
            </button>
            <button
              type="button"
              className="stamm-action-button stamm-delete-button"
              disabled={!canWrite || dialogBusy}
              aria-label={t`${label} löschen`}
              title={canWrite ? t`Löschen` : readOnlyNotice('cmdb.write')}
              onClick={(event) => {
                event.stopPropagation();
                void openDelete(row);
              }}
            >
              <Icon name="trash" size={18} />
            </button>
          </div>;
        },
      },
    ];
    // i18n.locale: the headers and cells carry translated text.
  }, [canImport, canWrite, classMeta, dialogBusy, openDelete, openForm, renderValue, t, i18n.locale]);

  const renderDetail = useCallback(
    (row: CmdbCardRow): ReactNode => {
      if (!classMeta) return null;
      return (
        <div className="stamm-detail">
          <div className="stamm-detail-heading"><strong>{row.label}</strong><span>#{row.id}</span></div>
          <dl className="stamm-detail-grid">
          {classMeta.attributes.map((attr) => (
            <div key={attr.name}>
              <dt title={attr.help}>
                {attr.label}
                {attr.system ? <Chip><Trans>System</Trans></Chip> : null}
              </dt>
              <dd>{renderValue(attr, row)}</dd>
            </div>
          ))}
          </dl>
        </div>
      );
    },
    // i18n.locale: the detail carries translated text.
    [classMeta, renderValue, i18n.locale],
  );

  /* --- render ----------------------------------------------------------- */

  if (loadingMeta) return <PageSpinner text={t`Lädt Stammdaten-Schema…`} />;

  if (metaError || !meta) {
    return (
      <>
        <PageHeader title={t`Stammdaten`} />
        <Banner
          kind="error"
          sub={t`Prüfen Sie die Verbindung zu CMDBuild und ob das Schema angelegt ist (System → Datenmodell & Demo).`}
        >
          {metaError ?? t`Schema konnte nicht geladen werden.`}
        </Banner>
      </>
    );
  }

  const grouped = GROUPS.map((group) => ({
    key: group.key,
    label: i18n._(group.label),
    entries: meta.classes.filter((entry) => group.classes.includes(entry.name)
      && !(hasComponentCatalog && entry.name === 'KomponenteVersion')
      && !(hasProductCatalog && entry.name === 'ProduktVersion')),
  }));
  const groupedNames = new Set(GROUPS.flatMap((group) => group.classes));
  const rest = meta.classes.filter((entry) => !groupedNames.has(entry.name));
  if (rest.length > 0) grouped.push({ key: 'other', label: t`Weitere`, entries: rest });

  const countOf = (klass: string): string => {
    const value = counts[klass];
    if (value === undefined) return DASH;
    return value < 0 ? '?' : String(value);
  };
  const listLabel = classMeta ? catalogLabel(classMeta) : t`Stammdaten`;
  const rowCount = rows.length;
  const classPlural = classMeta?.label_plural;
  let emptyTitle = t`Noch keine Datensätze`;
  if (search) emptyTitle = t`Keine Treffer gefunden`;
  else if (selected === 'CVE') emptyTitle = t`Noch keine gespeicherten CVEs`;
  else if (classPlural) emptyTitle = t`Noch keine ${classPlural}`;

  return (
    <div className="stamm-page">
      <PageHeader
        title={t`Stammdaten`}
        actions={has('import.write') ? <Link className="stamm-import" to="/import"><Icon name="download" /><Trans>Daten importieren</Trans></Link> : undefined}
      />

      {!canWrite ? <Banner kind="info">{readOnlyNotice('cmdb.write')}</Banner> : null}


      <div className="stamm-layout">
        <aside className="stamm-catalog" aria-label={t`Datenkatalog`}>
          <nav ref={catalogRef} className="stamm-classes" aria-label={t`Stammdaten-Kategorien`}>
            {grouped.map((group) => (
              <div className="stamm-group" key={group.key}>
                <div className="stamm-group-label">{group.label}</div>
                {group.entries.map((entry) => (
                  <button
                    key={entry.name}
                    type="button"
                    className={entry.name === catalogSelection ? 'on' : undefined}
                    aria-current={entry.name === catalogSelection ? 'page' : undefined}
                    title={classHint(entry.name) ?? undefined}
                    onClick={() => navigateClass(entry.name)}
                  >
                    <StammIcon name={CLASS_ICONS[entry.name] ?? 'database'} />
                    <span className="stamm-class-name">{catalogLabel(entry)}</span>
                    <Chip>{countOf(entry.name)}</Chip>
                  </button>
                ))}
              </div>
            ))}
          </nav>
          <div className="stamm-catalog-note"><StammIcon name="link" /><p><Link to="/beziehungen"><Trans>Beziehungen ansehen</Trans> <Icon name="external" size={12} /></Link></p></div>
        </aside>

        {isComponentCatalog ? <ComponentCatalog meta={meta} canWrite={canWrite} onCountsChange={loadCounts} renderValue={renderValue} />
          : isProductCatalog ? <ProductCatalog meta={meta} canWrite={canWrite} canImport={canImport} onCountsChange={loadCounts} renderValue={renderValue} />
          : isBomOverview ? <BomOverview canWrite={canWrite} onCountsChange={loadCounts} /> : (
        <section className="card stamm-content" aria-labelledby="stamm-list-title" aria-busy={loadingRows}>
          <div className="card-head table-head stamm-content-head">
            <div className="stamm-title-row">
              <h2 id="stamm-list-title">{listLabel}</h2>
              {hint ? <button type="button" className="icon-button stamm-info-toggle" aria-expanded={infoOpen} aria-controls="stamm-info" aria-label={t`Über diese Daten`} title={t`Über diese Daten`} onClick={() => setInfoOpen((open) => !open)}><Icon name="info" /></button> : null}
            </div>
            {classMeta?.can_create ? (
              <button
                type="button"
                className="primary stamm-create"
                disabled={!canWrite || dialogBusy}
                title={canWrite ? undefined : readOnlyNotice('cmdb.write')}
                onClick={() => void openForm(null)}
              >
                <Icon name="plus" /><Trans>Neu anlegen</Trans>
              </button>
            ) : classMeta ? (
              <span className="stamm-auto-note" title={t`Diese Datensätze entstehen automatisch und können hier nur bearbeitet oder gelöscht werden.`}><Trans>Entsteht automatisch</Trans></span>
            ) : null}
            {hint && infoOpen ? <p className="stamm-info" id="stamm-info">{hint}</p> : null}
          </div>
          <div className="table-toolbar">
            <input type="search" value={search} placeholder={t`${listLabel} durchsuchen…`} aria-label={t`Stammdaten durchsuchen`} onChange={(event) => { ++listSeq.current; setSearch(event.target.value); }} />
            <div className="table-toolbar-options">
              {selected === 'CVE' && has('dt.read') ? (
                <Link className="stamm-cve-link" to="/schwachstellen" title={t`Hier stehen nur gespeicherte CVEs. Alle erkannten Scan-Befunde zeigt die Seite „Schwachstellen“.`}><Trans>Erkannte CVEs anzeigen</Trans><Icon name="arrow-right" size={14} /></Link>
              ) : null}
              <span className="table-count" role="status" aria-live="polite">{loadingRows ? t`Wird geladen…` : <Plural value={rowCount} one="# Eintrag" other="# Einträge" />}</span>
            </div>
          </div>
          <div className="stamm-data">
            {listError ? <Banner kind="error">{listError}</Banner> : null}

            {loadingRows && rows.length === 0 ? (
              <PageSpinner text={t`Lädt Datensätze…`} />
            ) : rows.length === 0 && !listError ? (
              <EmptyState
                title={emptyTitle}
                action={search ? <button type="button" onClick={() => setSearch('')}><Trans>Suche zurücksetzen</Trans></button> : classMeta?.can_create && canWrite ? <button type="button" onClick={() => void openForm(null)} disabled={dialogBusy}><Icon name="plus" /><Trans>Ersten Datensatz anlegen</Trans></button> : undefined}
              >
                {search ? t`Versuchen Sie einen anderen Suchbegriff oder setzen Sie die Suche zurück.` : classMeta?.can_create && canWrite ? t`Legen Sie den ersten Datensatz an oder importieren Sie vorhandene Daten.` : undefined}
              </EmptyState>
            ) : (
              <DataTable
                rows={rows}
                columns={columns}
                rowKey={(row) => row.id}
                renderDetail={renderDetail}
                expandedKeys={expanded}
                onToggleRow={toggleRow}
                pageSize={10}
                paginationResetKey={`${selected}:${search}`}
                revealRowKey={focusId}
                mobileBreakpoint={760}
                caption={listLabel}
                rowClassName={(row) => (row.id === focusRow ? 'stamm-focus' : undefined)}
              />
            )}
          </div>
        </section>
        )}
      </div>

      {dialog.kind === 'form' && classMeta ? (
        <CardFormModal
          meta={classMeta}
          row={dialog.row}
          options={dialog.options}
          lookups={meta.lookups}
          onSubmit={submitForm}
          onClose={() => setDialog({ kind: 'closed' })}
        />
      ) : null}

      {dialog.kind === 'delete' && classMeta ? (
        <DeleteCardModal
          meta={classMeta}
          row={dialog.row}
          blockers={dialog.row.referenced_by ?? []}
          onConfirm={confirmDelete}
          onClose={() => setDialog({ kind: 'closed' })}
        />
      ) : null}
    </div>
  );
}

export default StammdatenPage;
